import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { RecruitingView } from '../../components/RecruitingView';

export function Recruiting() {
  const { profile } = useAuth();

  const [eligibleAthletes, setEligibleAthletes] = useState(null); // null = loading
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);

  useEffect(() => { if (profile) loadEligibleAthletes(); }, [profile]);

  async function loadEligibleAthletes() {
    const { data: aps } = await supabase
      .from('athlete_profiles')
      .select('id, grade_level')
      .eq('coach_id', profile.id)
      .in('grade_level', ['10th', '11th', '12th']);

    if (!aps?.length) { setEligibleAthletes([]); return; }
    const ids = aps.map(a => a.id);

    const { data: bills } = await supabase
      .from('billing')
      .select('athlete_id, tier, year, month')
      .in('athlete_id', ids)
      .order('year', { ascending: false })
      .order('month', { ascending: false });

    const latestTier = {};
    for (const b of (bills ?? [])) {
      if (!latestTier[b.athlete_id]) latestTier[b.athlete_id] = b.tier;
    }
    const eliteIds = ids.filter(id => latestTier[id] === 'elite');
    if (!eliteIds.length) { setEligibleAthletes([]); return; }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', eliteIds)
      .order('full_name');

    const gradeMap = Object.fromEntries(aps.map(a => [a.id, a.grade_level]));
    const list = (profiles ?? []).map(p => ({ ...p, grade_level: gradeMap[p.id] }));
    setEligibleAthletes(list);
    const saved = sessionStorage.getItem('apex_recruiting_athlete');
    const restore = saved && list.find(a => a.id === saved);
    setSelectedAthleteId(restore ? saved : list[0]?.id ?? null);
  }

  useEffect(() => {
    if (selectedAthleteId) sessionStorage.setItem('apex_recruiting_athlete', selectedAthleteId);
  }, [selectedAthleteId]);

  const selectedAthlete = eligibleAthletes?.find(a => a.id === selectedAthleteId);

  // Loading
  if (eligibleAthletes === null) {
    return (
      <Layout>
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      </Layout>
    );
  }

  if (eligibleAthletes.length === 0) {
    return (
      <Layout>
        <div style={{ marginBottom: 24 }}>
          <p className="page-eyebrow">NCAA Recruiting</p>
          <h1 className="page-title">Recruiting Hub</h1>
        </div>
        <div className="empty-state">
          <p className="empty-state-title">No eligible athletes</p>
          <p className="empty-state-sub">
            Recruiting is available for athletes in 10th–12th grade on the Elite plan.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="rec-toolbar">
        <div>
          <p className="page-eyebrow">NCAA Recruiting</p>
          <h1 className="page-title" style={{ marginBottom: 0 }}>Recruiting Hub</h1>
        </div>
        {eligibleAthletes.length > 1 && (
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Athlete</label>
            <select
              className="form-input form-select"
              style={{ minWidth: 200 }}
              value={selectedAthleteId ?? ''}
              onChange={e => setSelectedAthleteId(e.target.value)}
            >
              {eligibleAthletes.map(a => (
                <option key={a.id} value={a.id}>{a.full_name} ({a.grade_level})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedAthlete && eligibleAthletes.length === 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 10, padding: '12px 18px' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{selectedAthlete.full_name}</div>
          <span className="div-badge">{selectedAthlete.grade_level}</span>
        </div>
      )}

      {selectedAthleteId && (
        <RecruitingView
          athleteId={selectedAthleteId}
          athleteName={selectedAthlete?.full_name}
        />
      )}
    </Layout>
  );
}
